// A five-step reflection scale stores one icon ref per step in Question.IconNames,
// low to high, comma-separated: position 0 answers as -2 and position 4 as +2. A
// coach may leave steps out -- any two to five of them -- and a left-out step is an
// empty entry, so the remaining icons keep their values. Rows written before that was
// possible carry five refs and parse the same way.

export const SCALE_MIN = -2;
export const SCALE_MAX = 2;
export const SCALE_STEPS = SCALE_MAX - SCALE_MIN + 1;

export interface ScaleIcon {
  /** The answer value this step records, from -2 to 2. */
  value: number;
  /** Opaque icon ref, Font Awesome or `noto:` prefixed. */
  ref: string;
}

/** Every step of the scale, low to high; `undefined` where the coach left the step out. */
export const scaleIconSlots = (iconNames: string | null | undefined): (string | undefined)[] => {
  const parts = (iconNames ?? '').split(',').map((part) => part.trim());
  return Array.from({ length: SCALE_STEPS }, (_, i) => parts[i] || undefined);
};

/** Only the steps that carry an icon, with the value each one answers as. */
export const scaleIcons = (iconNames: string | null | undefined): ScaleIcon[] =>
  scaleIconSlots(iconNames).flatMap((ref, i) => (ref ? [{ value: i + SCALE_MIN, ref }] : []));

/** The stored form of a scale: one entry per step, blank where there is no icon. */
export const toScaleIconNames = (slots: readonly (string | undefined | null)[]): string =>
  Array.from({ length: SCALE_STEPS }, (_, i) => slots[i] ?? '').join(',');

export interface ScaleRange {
  /** Lowest step a chart shows, never below -2. */
  min: number;
  /** Highest step a chart shows, never above 2. */
  max: number;
}

/**
 * The steps a chart has to show. An answer can only land on a step with an icon, so the range
 * runs from the lowest icon to the highest; it widens to cover any plotted value that falls
 * outside them, and is the whole scale when fewer than two steps carry an icon.
 */
export const scaleChartRange = (
  iconNames: string | null | undefined,
  values: Iterable<number | null | undefined> = [],
): ScaleRange => {
  const steps = scaleIcons(iconNames).map(({ value }) => value);
  let min = steps.length >= 2 ? steps[0] : SCALE_MIN;
  let max = steps.length >= 2 ? steps[steps.length - 1] : SCALE_MAX;
  for (const value of values) {
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    min = Math.min(min, Math.max(SCALE_MIN, Math.floor(value)));
    max = Math.max(max, Math.min(SCALE_MAX, Math.ceil(value)));
  }
  return { min, max };
};

/** One entry per step of the range, high to low to match a y axis; blank where the step has no icon. */
export const scaleIconRows = (iconNames: string | null | undefined, range: ScaleRange): (string | undefined)[] =>
  scaleIconSlots(iconNames)
    .slice(range.min - SCALE_MIN, range.max - SCALE_MIN + 1)
    .reverse();

/**
 * The steps a chart draws as rows, low to high: every step in the range that carries an icon, plus
 * the range's ends when a plotted value pushed it past the icons. Rows sit evenly apart, so a scale
 * that leaves a step out shows no gap for it.
 */
export const scaleChartRows = (iconNames: string | null | undefined, range: ScaleRange): number[] => {
  const slots = scaleIconSlots(iconNames);
  const rows: number[] = [];
  for (let step = range.min; step <= range.max; step++) {
    if (slots[step - SCALE_MIN] || step === range.min || step === range.max) rows.push(step);
  }
  return rows;
};

/** One icon per row, high to low to match a y axis; blank for a row that only marks a range end. */
export const scaleRowIcons = (
  iconNames: string | null | undefined,
  rows: readonly number[],
): (string | undefined)[] => {
  const slots = scaleIconSlots(iconNames);
  return rows.map((step) => slots[step - SCALE_MIN]).reverse();
};

/**
 * Where a value sits among the rows, counted from the bottom row: on its own row for a step that
 * has one, and proportionally between the two rows around it otherwise, so an average between two
 * icons plots between them. A value past either end lands on that end row.
 */
export const scaleRowPosition = (rows: readonly number[], value: number): number => {
  const last = rows.length - 1;
  if (last < 1 || value <= rows[0]) return 0;
  if (value >= rows[last]) return last;
  const upper = rows.findIndex((row) => row >= value);
  const lower = upper - 1;
  return lower + (value - rows[lower]) / (rows[upper] - rows[lower]);
};
